import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  BarChart3,
  Boxes,
  Check,
  Eye,
  EyeOff,
  QrCode,
  ShieldCheck,
  Sparkles,
  UserPlus,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { api, formatIDR } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlanId = searchParams.get("plan") || "";
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(requestedPlanId);
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/plans/public").then((response) => {
      if (!mounted) return;
      const activePlans = (response.data || []).filter((plan) => plan.active !== false);
      setPlans(activePlans);
      if (!selectedPlanId && activePlans[0]?.id) setSelectedPlanId(activePlans[0].id);
    }).catch(() => setPlans([]));
    return () => { mounted = false; };
    // selectedPlanId is intentionally initialized only once from the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPlan = useMemo(() => plans.find((plan) => plan.id === selectedPlanId) || null, [plans, selectedPlanId]);
  const passwordChecks = useMemo(() => ({
    length: form.password.length >= 8,
    lowercase: /[a-z]/.test(form.password),
    uppercase: /[A-Z]/.test(form.password),
    number: /[0-9]/.test(form.password),
  }), [form.password]);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.email.trim()) return toast.error("Alamat email wajib diisi");
    if (!passwordChecks.length) return toast.error("Password minimal 8 karakter");
    if (!passwordChecks.lowercase) return toast.error("Password harus memiliki huruf kecil");
    if (!passwordChecks.uppercase) return toast.error("Password harus memiliki huruf besar");
    if (!passwordChecks.number) return toast.error("Password harus memiliki angka");
    if (form.password !== form.confirm_password) return toast.error("Konfirmasi password tidak sama");

    setLoading(true);
    try {
      const result = await register({
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        password: form.password,
      });
      if (selectedPlanId) sessionStorage.setItem("kraya_preferred_plan_id", selectedPlanId);
      toast.success(result?.message || "Pendaftaran berhasil. Verifikasi email Anda.");
      navigate(`/verify-email?email=${encodeURIComponent(form.email.trim())}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pendaftaran gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <div className="grid min-h-screen lg:grid-cols-[.9fr_1.1fr]">
        <section className="relative hidden overflow-hidden bg-slate-950 p-12 text-white lg:flex lg:flex-col lg:justify-between xl:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(37,99,235,.38),transparent_30%),radial-gradient(circle_at_80%_75%,rgba(34,211,238,.18),transparent_34%)]" />
          <Link to="/" className="relative w-fit"><BrandMark size="lg" variant="on-dark" /></Link>

          <div className="relative max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[.16em] text-cyan-200"><Sparkles className="h-3.5 w-3.5" /> Trial 14 hari</div>
            <h1 className="mt-7 text-6xl font-black leading-[.95] tracking-[-0.055em]">Mulai rapikan operasional bisnis dari hari pertama.</h1>
            <p className="mt-6 text-lg leading-8 text-slate-300">Buat akun mitra, verifikasi email, lalu siapkan outlet, produk, kasir, QRIS, dan tim Anda di KRAYA.</p>

            <div className="mt-9 grid grid-cols-2 gap-3">
              {[
                [QrCode, "Cash & QRIS"],
                [Boxes, "Stok real-time"],
                [BarChart3, "Laporan bisnis"],
                [ShieldCheck, "Role & audit"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[.05] p-4 text-sm font-semibold text-slate-200"><Icon className="h-5 w-5 text-cyan-300" />{label}</div>
              ))}
            </div>

            <div className="mt-6 rounded-[2rem] border border-white/10 bg-white/[.05] p-5">
              <div className="text-[10px] font-bold uppercase tracking-[.16em] text-slate-500">Onboarding ringkas</div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                {["Daftar", "Verifikasi", "Mulai operasional"].map((label, index) => <div key={label} className="rounded-xl bg-white/[.06] px-2 py-3"><div className="mx-auto mb-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[10px] font-black">{index + 1}</div><span className="text-slate-300">{label}</span></div>)}
              </div>
            </div>
          </div>
          <div className="relative text-xs text-slate-500">KRAYA. · Powered By AntSoft ID</div>
        </section>

        <section className="flex items-start justify-center p-5 sm:p-8 lg:p-12 xl:p-16">
          <div className="w-full max-w-2xl py-4 lg:py-0">
            <div className="flex items-center justify-between">
              <Link to="/" className="lg:hidden"><BrandMark /></Link>
              <Link to="/" className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-950"><ArrowLeft className="h-3.5 w-3.5" /> Beranda</Link>
            </div>

            <div className="mt-7 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_30px_80px_-45px_rgba(15,23,42,.45)] sm:p-9">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><UserPlus className="h-6 w-6" /></div>
                  <h2 className="mt-5 text-4xl font-black tracking-[-0.045em]">Buat akun KRAYA</h2>
                  <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">Gunakan email aktif. Trial 14 hari dimulai setelah Anda membuka tautan verifikasi dari KRAYA.</p>
                </div>
                <div className="hidden rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-emerald-700 sm:block">14-day trial</div>
              </div>

              {plans.length > 0 && (
                <div className="mt-7 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div><div className="text-[10px] font-black uppercase tracking-[.15em] text-blue-600">Paket setelah trial</div><div className="mt-1 text-sm font-bold text-slate-950">Pilih paket yang ingin Anda lanjutkan</div></div>
                    <select value={selectedPlanId} onChange={(event) => setSelectedPlanId(event.target.value)} className="rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-4 focus:ring-blue-100" data-testid="reg-plan-select">
                      {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name} · {formatIDR(plan.price_monthly)}/bln</option>)}
                    </select>
                  </div>
                  {selectedPlan && <div className="mt-3 flex flex-wrap gap-2 text-xs text-blue-900"><span className="rounded-full bg-white px-3 py-1.5 font-semibold">{selectedPlan.max_outlets} outlet</span><span className="rounded-full bg-white px-3 py-1.5 font-semibold">{selectedPlan.max_users} pengguna</span><span className="rounded-full bg-white px-3 py-1.5 font-semibold">{(selectedPlan.feature_details || []).length} fitur</span></div>}
                </div>
              )}

              <form onSubmit={submit} className="mt-7 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div><label className="mb-2 block text-sm font-bold text-slate-700">Nama lengkap</label><input data-testid="reg-name-input" required autoComplete="name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama Anda" /></div>
                  <div><label className="mb-2 block text-sm font-bold text-slate-700">Nama bisnis / mitra</label><input data-testid="reg-business-input" required value={form.business_name} onChange={(event) => setForm({ ...form, business_name: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama usaha" /></div>
                  <div><label className="mb-2 block text-sm font-bold text-slate-700">Email</label><input data-testid="reg-email-input" type="email" required autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="nama@bisnis.com" /></div>
                  <div><label className="mb-2 block text-sm font-bold text-slate-700">Nomor HP <span className="font-medium text-slate-400">(opsional)</span></label><input data-testid="reg-phone-input" autoComplete="tel" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="08xxxxxxxxxx" /></div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Password</label>
                    <div className="relative"><input data-testid="reg-password-input" type={showPassword ? "text" : "password"} required minLength={8} autoComplete="new-password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pr-12 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Min. 8 karakter" /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button></div>
                  </div>
                  <div><label className="mb-2 block text-sm font-bold text-slate-700">Konfirmasi password</label><input data-testid="reg-confirm-password-input" type={showPassword ? "text" : "password"} required minLength={8} autoComplete="new-password" value={form.confirm_password} onChange={(event) => setForm({ ...form, confirm_password: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Ulangi password" /></div>
                </div>

                {form.password && (
                  <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
                    {[
                      [passwordChecks.length, "8+ karakter"],
                      [passwordChecks.uppercase, "Huruf besar"],
                      [passwordChecks.lowercase, "Huruf kecil"],
                      [passwordChecks.number, "Angka"],
                    ].map(([ok, label]) => <div key={label} className={`flex items-center gap-1.5 rounded-xl px-2.5 py-2 font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}><Check className="h-3.5 w-3.5" />{label}</div>)}
                  </div>
                )}

                <button data-testid="reg-submit-btn" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  {loading ? "Mengirim email verifikasi..." : "Daftar & Verifikasi Email"}
                </button>
              </form>

              <div className="mt-7 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">Sudah punya akun? <Link to="/login" className="font-bold text-blue-600 hover:text-blue-700" data-testid="reg-to-login">Masuk ke KRAYA</Link></div>
              <div className="mt-5 flex items-center justify-center gap-2 text-[11px] font-semibold text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Email diverifikasi sebelum trial diaktifkan</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
